//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { themr } from 'react-css-themr';
import {
    CustomField,
    FieldDataType,
    FieldFormatDataType
} from 'common/models/internal/EntitySettings';
import classnames from 'classnames';
import { EnhancedField } from 'common/components/core/form/Field';
import StringUtils from 'common/utils/StringUtils';
import { translate } from 'react-i18next';
import { Select, SelectTypes } from 'common/components/core/select';
import Switch from 'common/components/core/switch';
import { OverflowDetectorPopover } from 'common/components/core/popover/OverflowDetectorPopover';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import theme from './CustomValuesInput.scss';

import type { List, Map } from 'immutable';

type Props = {
    t: (string, ?Object) => string,
    theme: Object,
    customFields: List<CustomField>,
    values: Map<string, ?string>,
    returnValuesAsMap?: boolean,
    shouldGroupFields?: boolean,
    getFilteredCustomFields?: Function,
    getExtraPropsForField?: Function,
    getProcessedValue?: Function,
    setKey?: Function,
    useBooleanFieldAsDropdown?: boolean,
    onChange: Object => void
};

export const componentWithIdFactory = ({ Component }) => {
    return class FieldComponentWithId extends React.Component<any> {
        handleChange = (value: Object) => {
            const { id, onChange } = this.props;
            onChange(value, id);
        };
        render() {
            return <Component {...this.props} onChange={this.handleChange} />;
        }
    };
};

const InputWithId = componentWithIdFactory({ Component: EnhancedField });
const SelectWithId = componentWithIdFactory({ Component: Select });
const SwitchWithId = componentWithIdFactory({ Component: Switch });

export const customValuesInputFactory = ({
    TextFieldComponent = InputWithId,
    SelectFieldComponent = SelectWithId,
    BooleanFieldComponent = SwitchWithId
}: Object) => {
    return class CustomValuesInput extends React.Component<Props> {
        static defaultProps = {
            shouldGroupFields: true,
            useBooleanFieldAsDropdown: false
        };

        handleInputValueChange = (value: string, id: string) => {
            this.handleCustomValueChange(id, value);
        };

        handleSelectValueChange = (value: { value: string }, id: string) => {
            const { getProcessedValue } = this.props;
            const processedValue = getProcessedValue
                ? getProcessedValue(value)
                : value?.value;
            this.handleCustomValueChange(id, processedValue);
        };

        handleBooleanValueChange = (value: boolean, id: string) => {
            this.handleCustomValueChange(id, value.toString());
        };

        handleCustomValueChange = (propertyKey: string, value: string) => {
            const {
                customFields,
                values,
                onChange,
                setKey,
                returnValuesAsMap
            } = this.props;
            //validate
            const customField = customFields.find(
                field => field.key === propertyKey
            );
            if (!customField) {
                throw new Error(
                    `no customfield found in entitySettings called ${propertyKey}`
                );
            }
            //end validation, no issues, proceed
            const newValues = values.set(
                propertyKey,
                StringUtils.isEmpty(value) ? null : value
            ); //Set as null if empty

            setKey && setKey(propertyKey);
            returnValuesAsMap
                ? onChange(Immutable.Map(newValues))
                : onChange(newValues);
        };

        renderSelectField = (field, value, options, extraProps) => {
            const { theme, ...others } = this.props;
            return (
                <SelectFieldComponent
                    {...others}
                    {...extraProps}
                    id={field.key}
                    noMarginBottom
                    theme={theme}
                    selectType={SelectTypes.Outline}
                    options={options}
                    openMenuOnFocus
                    blurInputOnSelect={false}
                    isSearchable={false}
                    isClearable
                    value={value}
                    displayValue={value ? field.getDisplayValue(value) : null}
                    onChange={this.handleSelectValueChange}
                />
            );
        };

        renderBooleanField = (field, value, extraProps) => {
            const { theme, ...others } = this.props;
            const label = field.getDisplayValue(value);
            return (
                <BooleanFieldComponent
                    {...others}
                    {...extraProps}
                    className={theme.switch}
                    theme={theme}
                    id={field.key}
                    label={label}
                    onChange={this.handleBooleanValueChange}
                    checked={value === 'true'}
                />
            );
        };

        renderTextField = (field, value, extraProps) => {
            const { theme, ...others } = this.props;
            return (
                <TextFieldComponent
                    {...others}
                    {...extraProps}
                    type="text"
                    element="input"
                    value={value}
                    error={field.validate(value)}
                    placeholder={extraProps.placeholder || field.name}
                    onChange={this.handleInputValueChange}
                    id={field.key}
                    theme={theme}
                />
            );
        };

        renderField(field: CustomField, values: Map<string, ?string>) {
            const {
                getExtraPropsForField,
                useBooleanFieldAsDropdown
            } = this.props;
            const value = values.get(field.key) || '';

            let extraProps = {};
            if (getExtraPropsForField) {
                extraProps = getExtraPropsForField(field);
            }

            const { dataType, format, possibleValues } = field.metadata;
            if (
                dataType === FieldDataType.String &&
                format === FieldFormatDataType.SingleChoice
            ) {
                const options = possibleValues
                    .map(value => ({ value, label: value }))
                    .toArray();

                return this.renderSelectField(
                    field,
                    value,
                    options,
                    extraProps
                );
            }
            if (dataType === FieldDataType.Boolean) {
                if (useBooleanFieldAsDropdown) {
                    let options = possibleValues
                        .map(value => ({
                            value: field.getActualValueForDropdown(value),
                            label: value
                        }))
                        .toArray();

                    if (options.length === 0) {
                        const defaultBooleanValues = field.getPossibleBooleanValues();
                        options = defaultBooleanValues
                            .map(value => {
                                return {
                                    label: field.getDisplayValue(value),
                                    value
                                };
                            })
                            .toArray();
                    }
                    return this.renderSelectField(
                        field,
                        value,
                        options,
                        extraProps
                    );
                }
                return this.renderBooleanField(field, value, extraProps);
            }
            return this.renderTextField(field, value, extraProps);
        }

        getDefaultFilteredFields = (
            fieldList: List<CustomField>
        ): List<CustomField> => {
            return fieldList.filter(
                field =>
                    field.metadata.visible && !field.metadata.isModelProperty
            );
        };

        renderFieldsInGroups = (
            fieldList: List<CustomField>,
            values: Map<string, ?string>
        ) => {
            const { theme } = this.props;
            const nodes = [];
            const groupedProperties = [];
            for (
                let fields = fieldList;
                fields.size > 0;
                fields = fields.slice(2)
            ) {
                groupedProperties.push(fields.take(2));
            }
            groupedProperties.forEach(properties => {
                const first = properties.first();
                const second = properties.get(1);
                nodes.push(
                    <React.Fragment key={first.key}>
                        <div
                            className={classnames(theme.fieldGroup, {
                                [theme.halfWidth]: !!second
                            })}
                        >
                            <OverflowDetectorPopover
                                invert
                                className={theme.label}
                                popOverClassName={theme.labelPopover}
                                overflowText={first.name}
                                preferPlace={PopoverPlaceEnum.Above}
                                theme={theme}
                            >
                                {first.name}
                            </OverflowDetectorPopover>
                            {this.renderField(first, values)}
                        </div>
                        {second ? (
                            <div
                                className={classnames(
                                    theme.fieldGroup,
                                    theme.halfWidth
                                )}
                            >
                                <OverflowDetectorPopover
                                    invert
                                    className={theme.label}
                                    popOverClassName={theme.labelPopover}
                                    overflowText={second.name}
                                    preferPlace={PopoverPlaceEnum.Above}
                                    theme={theme}
                                >
                                    {second.name}
                                </OverflowDetectorPopover>
                                {this.renderField(second, values)}
                            </div>
                        ) : (
                            []
                        )}
                    </React.Fragment>
                );
            });
            return nodes;
        };

        renderFields = (
            fieldList: List<CustomField>,
            values: Map<string, ?string>
        ) => {
            const { className } = this.props;
            const nodes = [];
            fieldList.forEach(field => {
                nodes.push(
                    <div className={className} key={field.key}>
                        {this.renderField(field, values)}
                    </div>
                );
            });
            return nodes;
        };

        render() {
            const {
                shouldGroupFields,
                customFields,
                values,
                getFilteredCustomFields
            } = this.props;
            const fields = getFilteredCustomFields
                ? getFilteredCustomFields(customFields)
                : this.getDefaultFilteredFields(customFields);
            if (shouldGroupFields) {
                return this.renderFieldsInGroups(fields, values);
            }
            return this.renderFields(fields, values);
        }
    };
};

const CustomValuesInput = customValuesInputFactory({});
//Not using the prop t here, used inside the internal model, hoc is just to make sure that it rerenders
export { CustomValuesInput, SelectWithId, SwitchWithId, InputWithId };
export default translate('validations')(themr(null, theme)(CustomValuesInput));
